// components/Interview.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as Sentry from "@sentry/browser";
import api, { APIException } from '../api';
import InterviewUI, { InterviewUIState } from './InterviewUI';
import InterviewStart from './InterviewStart';
import { useAirPlay } from '../hooks/useAirPlay';
import FullPageSpinner from './FullPageSpinner';
import { v4 as uuidv4 } from 'uuid';
import EditCommunicationPassAudioRef from './EditCommunicatioPassAudioRef';
import { scrollToTop } from '../utils/scrollToTop';
import { UseCase } from '../models/UseCase';
import { Interview, Section, Question } from '../models/Interview';
import { useLocation, useSearchParams } from 'react-router-dom';
import { logError, logWarning } from '../utils/log-exception';
import { toast } from 'react-toastify';
enum PageState {
  NotStarted = "NOT_STARTED",
  ReadingPreamble = "READING_PREAMBLE",
  ReadingPaused = "READING_PAUSED",
  RepeatQuestion = "REPEAT_QUESTION",
  ReadingQuestion = "READING_QUESTION",
  RecordingAnswer = "RECORDING_ANSWER",
  GeneratingSummary = "GENERATING_SUMMARY",
  ReadingSummary = "READING_SUMMARY",
  ReadingSummaryQuestion = "READING_SUMMARY_QUESTION",
  RecordingSummaryAnswer = "RECORDING_SUMMARY_ANSWER",
  GeneratingCommunications = "GENERATING_COMMUNICATIONS",
  PollingCommunications = "POLLING_COMMUNICATIONS",
  Finished = "FINISHED",
  Error = "ERROR",
}

interface InterviewState {
  pageState: PageState;
  actionStarted: boolean;
  currentSection: number;
  currentQuestion: number;
}

const MIME_TYPE = 'audio/mp4';
const REPEAT_AUDIO_URL = "https://bespoke-jesse.s3.us-west-1.amazonaws.com/static/repeat.mp3"

interface InterviewManagerProps {
  communicationRequestId?: string;
  interviewData: Interview;
  oneOnOne: boolean;
  useCase?: UseCase;
  onClose: () => void;
}

const calculateInitialInterviewState = (interview: Interview, useCase?: UseCase): InterviewState => {
  if (useCase) {
    // if there is a use case, we are starting from the the beginning.
    return {
      pageState: PageState.NotStarted,
      actionStarted: false,
      currentSection: 0,
      currentQuestion: 0
    }
  }
  // Loop through sections
  for (let sectionIndex = 0; sectionIndex < interview.sections.length; sectionIndex++) {
    const section = interview.sections[sectionIndex];

    // Loop through questions in section
    for (let questionIndex = 0; questionIndex < section.questions.length; questionIndex++) {
      if (!section.questions[questionIndex].answerAudio) {
        return {
          pageState: sectionIndex === 0 && questionIndex === 0 ? PageState.ReadingPreamble : PageState.ReadingQuestion,
          actionStarted: false,
          currentSection: sectionIndex,
          currentQuestion: questionIndex
        };
      }
    }

    // Check summary question if it exists
    if (section.summaryQuestion && !section.summaryQuestion.answerAudio) {
      return {
        pageState: PageState.ReadingSummaryQuestion,
        actionStarted: false,
        currentSection: sectionIndex,
        currentQuestion: section.questions.length - 1
      };
    }
  }

  // If all questions are answered, regenerate
  return {
    pageState: PageState.GeneratingCommunications,
    actionStarted: false,
    currentSection: interview.sections.length - 1,
    currentQuestion: interview.sections[interview.sections.length - 1].questions.length - 1
  };
};

const InterviewManager: React.FC<InterviewManagerProps> = ({ useCase, onClose, oneOnOne, interviewData: initialInterviewData, communicationRequestId }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [communication, setCommunication] = useState<any>(null)
  const [interviewData, setInterviewData] = useState({ ...initialInterviewData, oneOnOne: oneOnOne });
  const [interviewState, setInterviewState] = useState<InterviewState>(calculateInitialInterviewState(interviewData, useCase));
  const [pendingAnswerSaves, setPendingAnswerSaves] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [communicationGeneratingStatus, setCommunicationGeneratingStatus] = useState<string | null>(null)
  const { audioRef, isAirPlayAvailable, showAirPlayPicker } = useAirPlay()
  const [stateToReturnTo, setStateToReturnTo] = useState<PageState | null>(null)
  const [pollingForCommunication, setPollingForCommunication] = useState(false)
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();


  const initializeMediaRecorder = async () => {
    console.log('Initializing media recorder');
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const mediaRecorder = new MediaRecorder(stream, { mimeType: MIME_TYPE });
    mediaRecorderRef.current = mediaRecorder;
    return mediaRecorder
  }

  useEffect(() => {
    return () => {
      cleanupAudio();
      cleanupMediaRecorder();
    };
  }, []);

  function cleanupAudio() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
  }

  function cleanupMediaRecorder() {
    console.log('Cleaning up media recorder');
    if (mediaRecorderRef.current) {
      const mediaRecorder = mediaRecorderRef.current;
      mediaRecorder.ondataavailable = null;
      mediaRecorder.onstop = null;
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }

  const togglePauseReading = () => {
    if (interviewState.pageState === PageState.ReadingPaused) {
      setInterviewState(prevState => {
        const newState = { ...prevState };
        if (stateToReturnTo) {
          newState.pageState = stateToReturnTo;
        }
        return newState;
      });
      if (audioRef.current) {
        // Rewind by 5 seconds, or to the beginning if in the first 5 seconds
        audioRef.current.currentTime = 0; //Math.max(0, audioRef.current.currentTime - 5);
        audioRef.current.play();
      }
    } else {
      setStateToReturnTo(interviewState.pageState)
      setInterviewState(prevState => {
        const newState = { ...prevState };
        newState.pageState = PageState.ReadingPaused;
        return newState;
      });
      audioRef.current?.pause();
    }
  }

  const isSummary = useCallback(() => {
    return interviewState.pageState === PageState.GeneratingSummary ||
      interviewState.pageState === PageState.ReadingSummary ||
      interviewState.pageState === PageState.ReadingSummaryQuestion ||
      interviewState.pageState === PageState.RecordingSummaryAnswer;
  }, [interviewState]);

  const currentSection = useCallback(() => interviewData.sections[interviewState.currentSection], [interviewData, interviewState]);
  const currentQuestion = useCallback(() => currentSection().questions[interviewState.currentQuestion], [currentSection, interviewState]);
  const actionStarted = useCallback(() => {
    setInterviewState(prevState => {
      const newState = { ...prevState };
      newState.actionStarted = true;
      return newState;
    });
  }, []);

  const nextInterviewState = useCallback((reason: String, newPageState?: PageState, previousPageState?: PageState) => () => {
    console.log('Changing state ' + reason);
    const onLastQuestionInSection = (currentInterviewState: InterviewState) => {
      return currentInterviewState.currentQuestion === interviewData.sections[currentInterviewState.currentSection].questions.length - 1;
    }

    const onLastSection = (currentInterviewState: InterviewState) => {
      return currentInterviewState.currentSection === interviewData.sections.length - 1;
    }

    setInterviewState(prevState => {
      const newState = { ...prevState };
      newState.actionStarted = false;
      if (newPageState) {
        newState.pageState = newPageState;
        return newState
      }
      switch (previousPageState || prevState.pageState) {
        case PageState.NotStarted:
          newState.pageState = PageState.ReadingPreamble;
          break
        case PageState.ReadingPreamble:
          newState.pageState = PageState.ReadingQuestion;
          break;
        case PageState.ReadingQuestion:
          newState.pageState = PageState.RecordingAnswer;
          break;
        case PageState.RepeatQuestion:
          newState.pageState = PageState.ReadingQuestion;
          break;
        case PageState.RecordingAnswer:
          if (onLastQuestionInSection(prevState)) {
            newState.pageState = PageState.GeneratingSummary;
          } else {
            newState.pageState = PageState.ReadingQuestion;
            newState.currentQuestion += 1;
          }
          break;
        case PageState.GeneratingSummary:
          newState.pageState = PageState.ReadingSummary;
          break;
        case PageState.ReadingSummary:
          if (interviewData.sections[newState.currentSection].summaryQuestion) {
            newState.pageState = PageState.ReadingSummaryQuestion;
          } else if (onLastSection(prevState)) {
            newState.pageState = PageState.GeneratingCommunications;
          } else {
            newState.pageState = PageState.ReadingPreamble;
            newState.currentQuestion = 0;
            newState.currentSection += 1;
          }
          break;
        case PageState.ReadingSummaryQuestion:
          newState.pageState = PageState.RecordingSummaryAnswer;
          break;
        case PageState.RecordingSummaryAnswer:
          if (onLastSection(prevState)) {
            newState.pageState = PageState.GeneratingCommunications;
          } else {
            newState.pageState = PageState.ReadingPreamble;
            newState.currentQuestion = 0;
            newState.currentSection += 1;
          }
          break;
        case PageState.GeneratingCommunications:
          newState.pageState = PageState.PollingCommunications;
          break;
        case PageState.PollingCommunications:
          newState.pageState = PageState.Finished;
          break;
      }
      console.log('newState', newState)
      return newState;
    })
  }, [interviewData]);



  const processResponsiveQuestions = async () => {
    try {
      if (interviewData.id && interviewState.currentSection < interviewData.sections.length - 1) {
        const sectionsCompleted = interviewState.currentSection + 1
        const interviewId = interviewData.id
        console.log('Fetching responsive questions')
        const responsiveQuestions = await api.getResponsiveQuestions(interviewData.id, sectionsCompleted, interviewData)
        console.log('responsive questions ', responsiveQuestions)
        const nextTitle = interviewData.sections[sectionsCompleted].title
        const section = responsiveQuestions.sections.find((section: Section) => section.title === nextTitle)
        if (!section) {
          console.warn('No responsive questions found for ' + nextTitle)
          return
        }

        // Generate all audio URLs first
        const preamblePromise = (async () => {
          const audioUrl = await api.generateAudio(interviewId, sectionsCompleted, -1, section.preamble)
          section.preambleAudio = audioUrl
          return audioUrl
        })()

        const questionPromises = section.questions.map(async (question: Question, index: number) => {
          const audioUrl = await api.generateAudio(interviewId, sectionsCompleted, index + 1, question.question)
          question.questionAudio = audioUrl
          console.log("Audio generation complete for question ", index, " ", question.question)
          return audioUrl
        })

        // Wait for all audio generation to complete
        await Promise.all([preamblePromise, ...questionPromises])

        const sectionToChange = interviewData.sections[sectionsCompleted]
        section.questions.forEach((question: Question, index: number) => {
          if (sectionToChange) {
            if (index < sectionToChange.questions.length) {
              const questionToChange = sectionToChange.questions[index]
              console.log('In section', sectionToChange.title, ' replacing question ', index, ') ', questionToChange.question, ' with ', question.question)
              questionToChange.question = question.question;
              questionToChange.questionAudio = question.questionAudio;
            } else {
              console.log('In section', sectionToChange.title, ' adding question ', index, ') ', question.question)
              sectionToChange.questions.push({
                question: question.question,
                questionAudio: question.questionAudio
              });
            }
          }
        })
        setInterviewData(prevState => {
          const newState = { ...prevState };
          newState.sections[sectionsCompleted] = sectionToChange
          return newState;
        })
        // Now save the complete section with all audio URLs
        await api.saveResponsiveSection(interviewId, sectionsCompleted + 1, sectionToChange)
      }
    } catch (error) {
      // Interview can continue without this. 
      logWarning(error, "Error generating responsive questions");
    }
  }

  const readAudio = async (audioUrl: string | undefined | null, stateMessage: string) => {
    actionStarted();
    console.log('Reading audio ', audioUrl)
    //cleanupAudio();
    try {
      const next = nextInterviewState(stateMessage);
      if (audioRef.current && audioUrl) {
        const audio = audioRef.current
        audio.src = audioUrl;
        audio.onended = next
        await audio.play()
      } else {
        next();
      }
    } catch (e) {
      logError(e, "Error reading audio");
    }
  }

  useEffect(() => {
    let intervalId: any = null;
    let polls = 0;
    const getCommunications = async () => {
      if (!interviewData.id) {
        throw new Error('should not be polling without an interviewId')
      }
      try {
        const comms = await api.getCommunication(interviewData.id, true);
        polls += 1;
        console.log('Polling communication status', polls)
        if (!comms) {
          // this is fine, it just means the comms has not been created yet
          console.log('No communication found for interview yet (this is fine)', interviewData.id)
        } else if (comms.status === 'FINISHED') {
          clearInterval(intervalId);
          intervalId = null;
          setCommunication(comms);
          nextInterviewState("Got completed comms")()
          readAudio(comms.audioUrl, "Got completed comms")
        } else if (polls > 600) {
          // give up after 10 minutes
          clearInterval(intervalId);
          intervalId = null;
          console.error('Polling communications timed out');
        } else {
          setCommunication(comms)
          setCommunicationGeneratingStatus(comms.status)
        }
      } catch (error) {
        logWarning(error, "Error polling communications", { noToast: true });
      }
    }
    if (pollingForCommunication && !intervalId) {
      intervalId = setInterval(getCommunications, 1000);
    } else if (!pollingForCommunication && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    }
  }, [pollingForCommunication])

  useEffect(() => {
    const startRecording = async () => {
      actionStarted();
      cleanupMediaRecorder();
      console.log('Starting recording...');
      const chunks: Blob[] = [];
      const mediaRecorder = await initializeMediaRecorder();
      if (!mediaRecorder) {
        console.error('Media Recorder not initialized');
        return;
      }
      mediaRecorder.ondataavailable = (e) => {
        console.log('ondataavailable ' + e.data.size);
        chunks.push(e.data);
      }
      mediaRecorder.onstop = async () => {
        console.log('onstop ' + chunks.length);
        const audioBlob = new Blob(chunks, { type: MIME_TYPE });
        if (chunks.length === 0 || chunks.every((chunk) => chunk.size === 0)) {
          // re-ask the question.
          // TODO: should probably do this even when there is more then zero data.
          nextInterviewState("Heard Nothing", PageState.RepeatQuestion)();
          return;
        }
        nextInterviewState("Stopped Recording")();
        await submitAnswer(audioBlob, isSummary());
        console.log('on stop finished')
      };
      mediaRecorder.start();
    };

    const askQuestion = () => {
      actionStarted();
      console.log('Asking question')
      let audioSrc = isSummary() ? currentSection().summaryQuestion?.questionAudio : currentQuestion().questionAudio;
      console.log('Asking question ', audioSrc)
      readAudio(audioSrc, "Asking Question Finished");
    };

    const submitAnswer = async (audioBlob: Blob, summary: boolean) => {
      if (!interviewData.id) {
        throw new Error('Should not submit answer without interviewId')
      }
      setPendingAnswerSaves(prevState => prevState + 1)
      try {
        const response = await api.saveAndTranscribe(
          interviewData.id,
          interviewState.currentSection + 1,
          interviewState.currentQuestion + 1,
          summary,
          audioBlob
        );

        if (!response.text.trim()) {
          toast.warning('No words in previous answer, ignoring.');
        }
        setInterviewData(prevState => {
          const newState = { ...prevState };
          const question = summary ? newState.sections[interviewState.currentSection].summaryQuestion : newState.sections[interviewState.currentSection].questions[interviewState.currentQuestion]
          if (question) {
            question.answer = response.text;
            question.answerAudio = response.audioUrl;
            question.answerAudioLength = response.audioLength;
          }
          return newState;
        });
        await api.addAnswer(interviewData.id, interviewState.currentSection + 1,
          interviewState.currentQuestion + 1, summary, response.text, response.audioUrl, response.audioLength);
      } catch (error) {
        logError(error, "Error submitting answer");
      } finally {
        setPendingAnswerSaves(prevState => prevState - 1)
      }
    };

    const generateSummary = async () => {
      actionStarted();
      processResponsiveQuestions() // start running in the background
      try {
        const summary = await api.saveAndSummarize(interviewState.currentSection + 1, interviewData)
        setInterviewData(prevState => {
          const newState = { ...prevState };
          newState.sections[interviewState.currentSection].summary = summary.text;
          newState.sections[interviewState.currentSection].summaryAudio = summary.audioUrl;
          return newState;
        });
        nextInterviewState("Summary Finished")();
      } catch (error) {
        logError(error, "Error generating summary");
        // summary is not required to be generated, so we can move on but skip to the summary answer state
        nextInterviewState("Error generating summary", undefined, PageState.RecordingSummaryAnswer)();
      }
    }

    const generateCommunicationBackground = async () => {
      try {
        const regenerate = searchParams.get("regenerate") === "true";
        await api.generateCommunication(interviewData, communicationRequestId, regenerate);
      } catch (error) {
        // unfortunately we can't tell the difference between a 504 (timeout) which is okay and a real error
        // so we just ignore it and move on
      }
    }

    const generateCommunication = async () => {
      actionStarted();
      generateCommunicationBackground();
      nextInterviewState("Generate Communications Started", PageState.PollingCommunications)();
    }

    const pollCommunicationStatus = async () => {
      actionStarted();
      setPollingForCommunication(true);
    }

    const readSummary = async () => {
      readAudio(currentSection().summaryAudio, "Reading Summary Finished");
    };

    const readPreamble = async () => {
      readAudio(currentSection().preambleAudio, "Reading Preamble Finished");
    };

    const repeatQuestion = async () => {
      readAudio(REPEAT_AUDIO_URL, "Reading Repeat Finsihed");
    };

    if (!interviewState.actionStarted) {
      if (interviewState.pageState === PageState.ReadingPreamble) {
        readPreamble();
      } else if (interviewState.pageState === PageState.RepeatQuestion) {
        repeatQuestion();
      } else if (interviewState.pageState === PageState.ReadingQuestion ||
        interviewState.pageState === PageState.ReadingSummaryQuestion) {
        askQuestion();
      } else if (interviewState.pageState === PageState.RecordingAnswer ||
        interviewState.pageState === PageState.RecordingSummaryAnswer) {
        startRecording();
      } else if (interviewState.pageState === PageState.GeneratingSummary) {
        if (currentSection().skipSummary) {
          nextInterviewState("Skipping Summary")();
        } else if (pendingAnswerSaves === 0) {
          generateSummary();
        }
      } else if (interviewState.pageState === PageState.ReadingSummary) {
        readSummary();
      } else if (interviewState.pageState === PageState.GeneratingCommunications) {
        if (pendingAnswerSaves === 0) {
          generateCommunication();
        }
      } else if (interviewState.pageState === PageState.PollingCommunications) {
        pollCommunicationStatus();
      }
    }
  }, [interviewState, interviewData, pendingAnswerSaves, currentQuestion, currentSection, nextInterviewState, isSummary, actionStarted]);

  useEffect(() => {
    console.log('Interview State Updated:');
    console.log(JSON.stringify(interviewState, null, 2));
  }, [interviewState]);

  const updateQueryParams = () => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', 'new-value');

    window.history.replaceState(
      { ...window.history.state },
      '',
      `${location.pathname}?${newSearchParams.toString()}`
    );
  };

  const startInterview = async (otherParticipant?: string) => {
    scrollToTop()
    setIsLoading(true)
    if (!useCase) {
      throw new Error('useCase is required to start an interview')
    }
    try {
      await initializeMediaRecorder();
      const id = uuidv4()
      await api.createNewInterview(id, useCase.id, otherParticipant)
      // update the query params so that the interview loader can pick it up
      window.history.replaceState(
        { ...window.history.state },
        '',
        `${location.pathname}?interviewId=${id}`
      );
      setInterviewData(prevData => {
        return { ...prevData, id, otherParticipant }
      })
    } catch (error) {
      logError(error, "Failed to start interview");
      onClose();
    } finally {
      setIsLoading(false)
    }
    nextInterviewState("Starting Interview")();
  }

  const stopRecording = () => {
    console.log('Stopping recording...');
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  };

  const skipSpeaking = () => {
    console.log('Skip Speaking recording...');
    if (audioRef.current) {
      const audio = audioRef.current
      audio.pause();
    }
    nextInterviewState("Skip Speaking")();
  };

  const speakingStates = [
    PageState.ReadingPreamble, PageState.ReadingQuestion,
    PageState.ReadingSummary, PageState.ReadingSummaryQuestion,
    PageState.RepeatQuestion
  ];

  const listeningStates = [PageState.RecordingAnswer, PageState.RecordingSummaryAnswer];

  const processingStates = [
    PageState.GeneratingSummary, PageState.GeneratingCommunications,
    PageState.PollingCommunications
  ];
  const pausedStates = [
    PageState.ReadingPaused,
  ];
  const errorStates = [
    PageState.Error,
  ];

  const onBack = () => {
    console.log('onBack')
    // right now we only support back from listening states
    if (listeningStates.includes(interviewState.pageState)) {
      cleanupMediaRecorder();
      setInterviewState(prevState => {
        const newState = { ...prevState };
        newState.actionStarted = false;
        if (newState.pageState === PageState.RecordingAnswer) {
          newState.pageState = PageState.ReadingQuestion;
        } else if (newState.pageState === PageState.RecordingSummaryAnswer) {
          newState.pageState = PageState.ReadingSummaryQuestion;
        }
        return newState;
      });
    } else {
      console.warn('onBack called from non-listening state', interviewState.pageState)
    }
  }

  const interviewUIState = (): InterviewUIState => {

    if (speakingStates.includes(interviewState.pageState)) {
      return InterviewUIState.SPEAKING;
    } else if (listeningStates.includes(interviewState.pageState)) {
      return InterviewUIState.LISTENING;
    } else if (processingStates.includes(interviewState.pageState)) {
      return InterviewUIState.PROCESSING;
    } else if (pausedStates.includes(interviewState.pageState)) {
      return InterviewUIState.PAUSED;
    } else if (errorStates.includes(interviewState.pageState)) {
      return InterviewUIState.ERROR;
    } else {
      return InterviewUIState.PROCESSING
    }
  }
  let processingText = ''
  let actionLink = undefined
  let actionText = undefined
  if (interviewState.pageState === PageState.GeneratingCommunications || interviewState.pageState === PageState.PollingCommunications) {
    if (!communicationGeneratingStatus) {
      processingText = 'Saving Interview'
    } else if (communicationGeneratingStatus === 'GENERATING_SCRIPT') {
      processingText = 'Creating Feedback (1-2 minutes)'
    } else if (communicationGeneratingStatus === 'GENERATING_AUDIO') {
      processingText = 'Generating Audio (1-2 minutes)'
      actionLink = `/communication/${communication?.id}`
      actionText = 'View Feedback'
    } else {
      processingText = `Generating Feedback (${communicationGeneratingStatus})`
    }
  }

  const removeFirstSentence = (text: string | undefined): string => {
    if (!text) return '';

    // Split into sentences by looking for period + space or period + end of string
    const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g);

    // Return original if no sentences found or only one sentence
    if (!sentences || sentences.length <= 1) {
      return text;
    }

    // Check if first sentence ends with period (not ? or !) and is less than 20 chars
    if (!sentences[0].trim().endsWith('.') || sentences[0].trim().length >= 20) {
      return text;
    }

    // Remove first sentence and trim any leading whitespace
    return sentences.slice(1).join('').trim();
  };


  const questionToDisplay = () => {
    switch (interviewState.pageState) {
      case PageState.ReadingQuestion:
      case PageState.RecordingAnswer:
        return removeFirstSentence(currentQuestion()?.question)
      case PageState.ReadingSummaryQuestion:
      case PageState.RecordingSummaryAnswer:
        return removeFirstSentence(currentSection().summaryQuestion?.question)
      default:
        return ''
    }
  }

  const title = currentSection().title;
  const uiState = interviewUIState();
  console.log('interviewState', interviewState)
  return (
    <>
      <audio ref={audioRef} />
      {isLoading ? <FullPageSpinner /> :
        interviewState.pageState === PageState.NotStarted && useCase ? (
          <InterviewStart useCase={useCase} recipient={interviewData.recipient} otherParticipant={interviewData.otherParticipant} oneOnOne={oneOnOne} title={interviewData.title} onStart={startInterview} onClose={onClose} />
        ) :
          interviewState.pageState === PageState.Finished ? (
            <EditCommunicationPassAudioRef communication={communication} audioRef={audioRef} startsPlaying={true} />
          ) :
            <InterviewUI onClose={onClose} title={title} state={uiState} onSkipSpeaking={skipSpeaking}
              onStopRecording={stopRecording}
              isAirPlayAvailable={isAirPlayAvailable}
              showAirPlayPicker={showAirPlayPicker}
              togglePauseReading={togglePauseReading}
              onBack={onBack}
              processingText={processingText}
              actionLink={actionLink}
              actionText={actionText}
              question={questionToDisplay()}
            />}
    </>
  );
};

export default InterviewManager;